/**
 * План агроцикла с сервера (ядро S4): шапка-строка цикла, KPI слоя метрик,
 * «Скоро», план по этапам (проекция шаблона до старта, своя копия после),
 * «Начать агроцикл» со сверкой. Правка плана менеджером живёт на шкале
 * (`cycle/CyclePlanWidget` + `usePlanEditing`): здесь форм со сдвигом дат нет.
 *
 * Клиент ничего не считает: этапы, окна, «впереди», участники и доли — ответ
 * `GET /agrocycles/{key}`. Кнопки — по подсказке `/auth/me`
 * (`can_edit_agrocycle`); право проверяет сервер (хоким только читает, №796).
 */

import { useState, type ReactNode } from 'react';
import { CalendarClock, Play } from 'lucide-react';
import { Card } from '../../ui/Card';
import { useAnalysisContext } from '../../../analysis/context';
import { useRequest } from '../../../api/useRequest';
import { ApiError } from '../../../api/client';
import { COMMON } from '../../../i18n/common';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import { agrocycleApi, type AgrocycleDetail, type StartCheck } from './agrocycleApi';
import { planLine, warningSummary, windowText } from './agrocycleView';
import { dateShort } from '../../lib/format';
import { CycleSources } from './CycleSources';
import './agrocycle.css';

const m = defineMessages({
  failed: {
    id: 'cycle.plan.failed',
    defaultMessage: 'Не получилось — повторите.',
    description: 'Ошибка рядом с кнопкой действия над планом агроцикла (менеджер), когда сервер не дал своего текста',
  },
  start: { id: 'cycle.plan.start.button', defaultMessage: 'Начать агроцикл', description: 'Кнопка в шапке плана (менеджер): открыть сверку перед стартом агроцикла' },
  startTitle: { id: 'cycle.plan.start.title', defaultMessage: 'Начать агроцикл', description: 'Заголовок карточки сверки перед стартом агроцикла (менеджер)' },
  changed: {
    id: 'cycle.plan.start.changed',
    defaultMessage: '<b>Шаблон изменился, пока вы смотрели план.</b> Изменения: {changes}. Ниже — новый план; начните ещё раз.',
    description: 'Предупреждение в сверке старта (менеджер): шаблон поменяли; <b> — жирное первое предложение; {changes} — перечень изменений из данных',
  },
  changesFallback: {
    id: 'cycle.plan.start.changesFallback',
    defaultMessage: 'виды работ или год',
    description: 'Подстановка в «Изменения: …», когда сервер не назвал, что изменилось',
  },
  summary: {
    id: 'cycle.plan.start.summary',
    defaultMessage:
      'План шаблона (ревизия {revision}) на {year} год: {stages, plural, one {# этап} few {# этапа} many {# этапов} other {# этапа}}, {works, plural, one {# работа плана} few {# работы плана} many {# работ плана} other {# работы плана}}. После старта правки шаблона на этот агроцикл не влияют.',
    description: 'Сводка в сверке старта (менеджер): {revision} — номер ревизии шаблона, {year} — год привязки (строкой), {stages}/{works} — числа этапов и работ плана',
  },
  warningsTitle: {
    id: 'cycle.plan.start.warnings',
    defaultMessage: 'Поля, которые не войдут в охват (ошибка размещения — правится размещением):',
    description: 'Заголовок списка конфликтов размещения в сверке старта (менеджер)',
  },
  warningRow: {
    id: 'cycle.plan.start.warningRow',
    defaultMessage: '{reason}: {count}',
    description: 'Строка списка конфликтов размещения: {reason} — причина словами, {count} — сколько полей',
  },
  noWarnings: { id: 'cycle.plan.start.noWarnings', defaultMessage: 'Конфликтов размещения нет.', description: 'Сверка старта: конфликтов размещения не найдено' },
  cannotStart: {
    id: 'cycle.plan.start.cannot',
    defaultMessage: 'Начать нельзя: {reason}',
    description: 'Сверка старта (менеджер): почему старт недоступен; {reason} — причина (фраза «можно с …» или код причины из данных)',
  },
  tooEarly: {
    id: 'cycle.plan.start.tooEarly',
    defaultMessage: 'можно с {date}',
    description: 'Причина в «Начать нельзя: …»: слишком рано; {date} — дата, с которой можно начать',
  },
  confirm: { id: 'cycle.plan.start.confirm', defaultMessage: 'Подтвердить старт', description: 'Кнопка в сверке старта (менеджер): подтвердить старт агроцикла' },
  panelTitle: { id: 'cycle.plan.title', defaultMessage: 'План агроцикла', description: 'Заголовок карточки плана агроцикла' },
  started: { id: 'cycle.plan.started', defaultMessage: 'Агроцикл начат: план скопирован из шаблона.', description: 'Уведомление после старта агроцикла (менеджер)' },
  sourcesTitle: { id: 'cycle.plan.sources.title', defaultMessage: 'Работы по источнику', description: 'Заголовок карточки: работы агроцикла по источнику (план, предписания, добровольные)' },
  sourcesInfo: {
    id: 'cycle.plan.sources.info',
    defaultMessage:
      'План, предписания карантина и добровольные работы — раздельно: в «Выполнено» входят только работы на полях запущенных кампаний плана. Добровольные — учёт хозяйства, без доли выполнения и без сравнения.',
    description: 'Подсказка (i) к карточке «Работы по источнику»',
  },
  upcomingTitle: { id: 'cycle.plan.upcoming.title', defaultMessage: 'Скоро', description: 'Заголовок карточки: работы плана, у которых окно впереди' },
  upcomingInfo: {
    id: 'cycle.plan.upcoming.info',
    defaultMessage:
      'Работы плана без кампании, окно которых откроется в ближайшие {days, plural, one {# день} few {# дня} many {# дней} other {# дня}} или примерно. Работ на полях по ним ещё нет.',
    description: 'Подсказка (i) к карточке «Скоро»; {days} — число дней горизонта',
  },
  upcomingItem: { id: 'cycle.plan.upcoming.item', defaultMessage: '{label} — {window}', description: 'Строка в «Скоро»: название работы плана и её окно' },
  upcomingItemAhead: { id: 'cycle.plan.upcoming.itemAhead', defaultMessage: '{label} — {window} (впереди)', description: 'Строка в «Скоро»: название работы плана и её окно; окно пока дальше горизонта' },
  upcomingFinished: { id: 'cycle.plan.upcoming.finished', defaultMessage: 'Агроцикл завершён — впереди работ плана нет.', description: 'Пустое состояние «Скоро»: агроцикл завершён' },
  upcomingEmpty: {
    id: 'cycle.plan.upcoming.empty',
    defaultMessage:
      'В ближайшие {days, plural, one {# день} few {# дня} many {# дней} other {# дня}} новых работ плана нет: {plan}.',
    description: 'Пустое состояние «Скоро»; {days} — число дней, {plan} — строка «запущено K из M работ плана · впереди N»',
  },
  planProjection: { id: 'cycle.plan.card.projection', defaultMessage: 'План (проекция шаблона)', description: 'Заголовок карточки плана, пока агроцикл не начат: план — проекция шаблона' },
  planProjectionInfo: {
    id: 'cycle.plan.card.projectionInfo',
    defaultMessage: 'Пока агроцикл не начат, план — шаблон культуры (ревизия {revision}) на {year} год; правка шаблона видна сразу.',
    description: 'Подсказка (i) к карточке плана до старта; {revision} — номер ревизии шаблона, {year} — год',
  },
  planOwnInfo: {
    id: 'cycle.plan.card.ownInfo',
    defaultMessage: 'Свой план агроцикла: правки шаблона на него не влияют; каждая правка — в журнале плана.',
    description: 'Подсказка (i) к карточке плана после старта',
  },
  noStages: { id: 'cycle.plan.card.noStages', defaultMessage: 'В плане нет этапов.', description: 'Пустое состояние плана: этапов нет' },
});

type Params = Record<string, string | number | boolean | null | undefined>;

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : tr(m.failed);
}

/* ------------------------------------------------------------- старт */

/**
 * Старт агроцикла (менеджер): кнопка — в группе действий шапки страницы,
 * сверка — карточкой под шапкой. Возвращает обе части раздельно.
 */
export function useStartFlow(detail: AgrocycleDetail | null, params: Params, onDone: (d: AgrocycleDetail, note: string) => void): { trigger: ReactNode; panel: ReactNode } {
  const t = useT();
  const [check, setCheck] = useState<StartCheck | null>(null);
  const [changes, setChanges] = useState<{ kind: string; what: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      setCheck(await agrocycleApi.startCheck(detail.key, params));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!check || !detail) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await agrocycleApi.start(detail.key, check.token, params), t(m.started));
      setCheck(null);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'template_changed') {
        // Шаблон изменился, пока смотрели: показать разницу и новый план,
        // нажать ещё раз — уже по нему.
        setChanges((e.details?.changes as { kind: string; what: string }[]) ?? []);
        setCheck(await agrocycleApi.startCheck(detail.key, params));
      } else {
        setError(errorText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const trigger = (
    <button type="button" className="astra-button astra-button-primary" onClick={open} disabled={busy || !!check}>
      <Play size={16} /> {t(m.start)}
    </button>
  );
  if (!check) {
    return { trigger, panel: error ? <p className="agc-error" role="alert">{error}</p> : null };
  }
  const groups = warningSummary(check.warnings);
  return { trigger, panel: (
    <Card tone="soft" className="agc-start" title={t(m.startTitle)}>
      {changes ? (
        <div className="agc-changed" role="alert">
          {t(m.changed, {
            b: (c) => <strong>{c}</strong>,
            changes: changes.length ? changes.map((c) => `${c.kind} ${c.what}`.trim()).join('; ') : t(m.changesFallback),
          })}
        </div>
      ) : null}
      <p>
        {t(m.summary, {
          revision: String(check.template_revision),
          year: String(check.anchor_year),
          stages: check.plan.stages.length,
          works: check.plan.stages.reduce((n, s) => n + s.works.length, 0),
        })}
      </p>
      {check.problems.length ? (
        <ul className="agc-problems">
          {check.problems.map((p, i) => <li key={i}>{String((p as { message?: string }).message ?? '')}</li>)}
        </ul>
      ) : null}
      {groups.length ? (
        <div className="agc-warnings">
          <strong>{t(m.warningsTitle)}</strong>
          <ul>{groups.map((g) => <li key={g.reason}>{t(m.warningRow, { reason: g.label, count: g.count })}</li>)}</ul>
        </div>
      ) : (
        <p className="agc-muted">{t(m.noWarnings)}</p>
      )}
      {!check.can_start ? (
        <p className="agc-error">
          {t(m.cannotStart, {
            reason: check.reason === 'too_early' && check.allowed_from ? t(m.tooEarly, { date: dateShort(check.allowed_from) }) : String(check.reason ?? ''),
          })}
        </p>
      ) : null}
      <div className="agc-actions">
        <button type="button" className="astra-button" onClick={confirm} disabled={busy || !check.can_start}>
          {t(m.confirm)}
        </button>
        <button type="button" className="astra-link" onClick={() => { setCheck(null); setChanges(null); }}>
          {t(COMMON.cancel)}
        </button>
        {error ? <span className="agc-error">{error}</span> : null}
      </div>
    </Card>
  ) };
}

/* ------------------------------------------------------------- данные */

type Analysis = { params: Params };

/**
 * Строка агроцикла с сервера и её локальная замена после правки менеджером.
 * Один запрос на страницу: шапка, «Скоро» и виджет плана читают один ответ.
 */
export function useAgrocycle(cycleKey: string | null) {
  const { params } = useAnalysisContext() as unknown as Analysis;
  const [override, setOverride] = useState<AgrocycleDetail | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const req = useRequest(async () => {
    if (!cycleKey) return null;
    const found = await agrocycleApi.detail(cycleKey, params);
    setOverride(null);
    return found;
  }, [cycleKey, JSON.stringify(params)]);
  const detail = override ?? req.data ?? null;
  const update = (d: AgrocycleDetail, message?: string) => {
    setOverride(d);
    setNote(message ?? null);
  };
  return { detail, req, update, note, params };
}

export type AgrocycleState = ReturnType<typeof useAgrocycle>;

/* ---------------------------------------------------- по источнику */

/**
 * Работы по источнику (план · предписания · добровольные) с основой каждой
 * доли. Шапка и факты объёма — в `CycleHead`; оценочных долей наверху нет
 * (замечание владельца 08.10).
 */
export function AgrocycleSources({ state }: { state: AgrocycleState }) {
  const t = useT();
  const { detail, note } = state;
  if (!detail) return null;
  return (
    <>
      {note ? <p className="agc-note" role="status">{note}</p> : null}
      {detail.sources?.length ? (
        <Card title={t(m.sourcesTitle)} info={t(m.sourcesInfo)}>
          <CycleSources sources={detail.sources} />
        </Card>
      ) : null}
    </>
  );
}

/** «Скоро»: работы плана без кампании, окно которых наступает в горизонте. */
export function AgrocycleUpcoming({ detail }: { detail: AgrocycleDetail }) {
  const t = useT();
  const k = detail.kpis;
  return (
    <Card title={t(m.upcomingTitle)} info={t(m.upcomingInfo, { days: detail.upcoming_days })}>
      {detail.upcoming.length ? (
        <ul className="agc-upcoming agc-upcoming--chips">
          {detail.upcoming.map((w) => (
            <li key={w.id} className={w.upcoming === 'soon' ? 'is-soon' : undefined}>
              <CalendarClock size={14} /> {t(w.upcoming === 'ahead' ? m.upcomingItemAhead : m.upcomingItem, { label: w.label, window: windowText(w) })}
            </li>
          ))}
        </ul>
      ) : (
        <p className="agc-muted">
          {detail.status === 'finished'
            ? t(m.upcomingFinished)
            : t(m.upcomingEmpty, { days: detail.upcoming_days, plan: planLine(k) })}
        </p>
      )}
    </Card>
  );
}

/** Проекция шаблона или свой план — подпись (i) к виджету плана. */
export function planInfo(detail: AgrocycleDetail): { title: string; info: string; empty: string } {
  const projection = detail.plan.mode === 'projection';
  return {
    title: projection ? tr(m.planProjection) : tr(m.panelTitle),
    info: projection
      ? tr(m.planProjectionInfo, { revision: String(detail.plan.template_revision), year: String(detail.anchor_year) })
      : tr(m.planOwnInfo),
    empty: tr(m.noStages),
  };
}
